import { Button, Card, Flex, Input, Table, message, Modal } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import debounce from "lodash/debounce";

import UserCircleAddIcon from "../../../../../../Theme/CustomIcons/Icons/Users/UserCircleAddIcon";
import TableEditIcon from "../../../../../../Theme/CustomIcons/Icons/CustomIcons/TableEditIcon";
import EditIcon from "../../../../../../Theme/CustomIcons/Icons/ContentEdit/EditIcon";
import TrashIcon from "../../../../../../Theme/CustomIcons/Icons/Essetional/TrashIcon";
import CustomLoaderIcon from "../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";

import AddUserDrawer from "../Drawers/AddUserDrawer";
import {
  getAllUserDataAction,
  userStatusChangeAction,
} from "../../../../../../store/action/admin/users.action";

const ActiveUsers = () => {
  const dispatch = useDispatch();
  const themeDefault = useSelector((state) => state.themeDefault);
  const { loading, info } = useSelector((state) => state.getALLUserList);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  //delete user
  const { confirm } = Modal;

  const handleBlockUser = (userId, name) => {
    confirm({
      title: `Are you sure you want to block ${name}?`,
      content: "This action can be reverted later by an admin.",
      okText: "Yes, block",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const payload = { status: "blocked" };
          await dispatch(userStatusChangeAction(payload, userId));
          message.success(`${name} has been blocked.`);
          fetchUsers(currentPage, searchTerm); // refresh table
        } catch (err) {
          message.error("Failed to block user.");
          console.error(err);
        }
      },
    });
  };

  // Fetch users
  const fetchUsers = (page = 1, search = "") => {
    dispatch(getAllUserDataAction(page, 10, search));
  };

  const debouncedSearch = useCallback(
    debounce((value) => {
      setCurrentPage(1);
      fetchUsers(1, value);
    }, 500),
    []
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    fetchUsers(currentPage, searchTerm);
  }, [currentPage]);

  const users = info?.users || [];
  const pagination = info?.pagination || {};

  const columns = [
    {
      title: "Full Name",
      key: "fullName",
      width: "30%",
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: "Display Name",
      dataIndex: "displayName",
      key: "displayName",
      width: "30%",
    },
    {
      title: "Licenses",
      dataIndex: "licenses",
      key: "licenses",
      width: "30%",
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      render: (_, record) => (
        <Flex align="center" gap={8}>
          <EditIcon
            size="small"
            onClick={() => {
              setSelectedUser(record); // set user for editing
              setIsDrawerOpen(true); // open drawer
            }}
          />
          <TrashIcon
            size="small"
            onClick={() =>
              handleBlockUser(
                record._id,
                `${record.firstName} ${record.lastName}`
              )
            }
          />
        </Flex>
      ),
    },
  ];

  return (
    <Card
      title="Active Users"
      extra={
        <Button
          type="primary"
          onClick={() => {
            setSelectedUser(null); // clear selected user for create
            setIsDrawerOpen(true);
          }}>
          <UserCircleAddIcon size="small" />
          Add a User
        </Button>
      }>
      <Flex
        justify="space-between"
        align="center"
        style={{
          width: "100%",
          borderTop: themeDefault?.info?.newConfig?.cardborder,
          paddingTop: 16,
          marginBottom: 16,
        }}>
        <Flex gap={8} align="center">
          <Input
            placeholder="Search for a user"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <Button onClick={() => fetchUsers(1, searchTerm)}>Filter</Button>
        </Flex>

        <Button>
          <TableEditIcon size="small" />
          Manage Columns
        </Button>
      </Flex>

      <Table
        size="small"
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={{
          spinning: loading,
          indicator: (
            <div
              style={{
                justifyContent: "center",
                alignItems: "center",
              }}>
              <CustomLoaderIcon />
            </div>
          ),
        }}
        pagination={{
          current: pagination.currentPage || 1,
          total: pagination.totalUsers || 0,
          pageSize: pagination.limit || 10,
          onChange: (page) => setCurrentPage(page),
          position: ["bottomCenter"],
        }}
      />

      {/* Single drawer for both create and edit */}
      <AddUserDrawer
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedUser(null);
        }}
        userId={selectedUser?._id}
      />
    </Card>
  );
};

export default ActiveUsers;
